import { catalogGaugeShares } from '@obraplan/core/domain/takeoff/consolidation';
import {
  computeGeometry,
  summarizeGeometry,
  type GeometryTotals,
  type TakeoffGeometry,
} from '@obraplan/core/domain/takeoff/geometry';
import {
  foundationSteelRates,
  type FoundationFactors,
} from '@obraplan/core/domain/takeoff/foundation-factors';
import { groundBeamTotals } from '@obraplan/core/domain/takeoff/foundation-totals';
import {
  retainingColumnTotals,
  retainingWallTotals,
} from '@obraplan/core/domain/takeoff/retaining-totals';
import { resolveServiceQuantity } from '@obraplan/core/domain/takeoff/service-quantity';
import type { ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import type { SlabOpening } from '@obraplan/core/domain/takeoff/slab';
import { withSteelRate, type SteelGauge } from '@obraplan/core/domain/takeoff/steel';
import {
  baseDoIndice,
  comAsPecas,
  pecasDosServicos,
  semPecasImportadas,
  type ServicoComPecas,
} from './pecas-importadas';
import { taxaDoServico } from './taxa-por-elemento';
import {
  commonAreaPositions,
  neighbourRoomsFor,
  partyWallsTouching,
  positionOfTypology,
  structureOf,
  typologiesOfDrawing,
  type TypologyDraft,
  type WorkspaceDraft,
} from './typology-draft';

/**
 * O medido de cada serviço, em qualquer tipologia (Fase 24, fatia 6).
 *
 * O item calculado é o medido do serviço vezes o índice, e o medido depende do desenho da
 * tipologia — que a tela só calculava para a que está na mesa. A soma da obra precisa do
 * medido de **todas**, e é daqui que ele sai: a mesma conta da mesa, feita para cada uma.
 */

/**
 * Os shafts do andar como buracos de laje (Fase 27, etapa 3, RN-370).
 *
 * O shaft é do ambiente do apartamento e a laje é da área comum do andar: o buraco que a laje
 * perde está nas outras tipologias **do mesmo desenho** (`floorKind`). Entra o que a conta do
 * shaft já aceita — o não excluído, de ambiente vivo da própria tipologia —, e nada de outro
 * pavimento: o shaft da cobertura não fura a laje do tipo.
 */
function buracosDoAndar(
  workspace: WorkspaceDraft,
  typology: TypologyDraft,
): readonly SlabOpening[] {
  return typologiesOfDrawing(workspace, typology).flatMap((doAndar) => {
    const vivos = new Set(
      doAndar.rooms.filter((room) => room.deletedAt === undefined).map((room) => room.id),
    );
    return (doAndar.shafts ?? [])
      .filter((shaft) => shaft.deletedAt === undefined && vivos.has(shaft.roomId))
      .map((shaft): SlabOpening => ({ from: shaft.from, to: shaft.to, count: shaft.count }));
  });
}

/**
 * A espessura da laje do andar, para a viga contar só abaixo dela (Fase 27, etapa 3, RN-371).
 *
 * A laje do próprio apartamento vem primeiro: a viga dele mora embaixo dela. Sem ela, vale a
 * **primeira** laje viva da área comum do desenho (F3), que cobre os aptos do andar. Sem laje
 * desenhada, `undefined` — e a viga fica como sempre foi (F13).
 */
function espessuraDaLajeDoAndar(
  workspace: WorkspaceDraft,
  typology: TypologyDraft,
): number | undefined {
  const primeiraLaje = (doAndar: TypologyDraft) =>
    (doAndar.slabs ?? []).find((peca) => peca.kind === 'LAJE' && peca.deletedAt === undefined);
  const propria = primeiraLaje(typology);
  if (propria !== undefined) return propria.thickness;
  for (const doAndar of typologiesOfDrawing(workspace, typology)) {
    if (doAndar.kind !== 'AREA_COMUM') continue;
    const laje = primeiraLaje(doAndar);
    if (laje !== undefined) return laje.thickness;
  }
  return undefined;
}

/**
 * A conta do desenho de uma tipologia, já contando as divisas com as vizinhas.
 *
 * É a conta que a montagem sempre fez para a tipologia da mesa, trazida para cá para servir
 * às outras também: a divisa entra inteira no desenho, e `ownPositionIds` diz qual metade da
 * alvenaria e do concreto é desta tipologia (RN-163).
 *
 * A peça desenhada com o nome de uma peça da planilha por peça **não** entra (Fase 28, RN-384):
 * a quantidade dela já veio pronta, e medi-la de novo contaria duas vezes. Ela continua, porém,
 * descontando a vizinha — o baldrame que entra no bloco B24 importado ainda perde o trecho
 * dentro dele (RN-368), e o muro, o do pilar de contenção (RN-372): a peça existe na obra, só
 * não é o desenho que a conta.
 */
export function geometriaDaTipologia(
  workspace: WorkspaceDraft,
  typology: TypologyDraft,
): TakeoffGeometry {
  const estrutura = structureOf(workspace, typology);
  const temLaje = (typology.slabs ?? []).some((peca) => peca.kind === 'LAJE');
  const sobAsVigas = espessuraDaLajeDoAndar(workspace, typology);
  // As peças que o desenho mede: sem as que vieram prontas da planilha por peça (RN-384).
  const desenhadas = semPecasImportadas(typology);
  const geometria = computeGeometry({
    rooms: typology.rooms,
    walls: [...typology.walls, ...partyWallsTouching(workspace, typology)],
    openings: [...typology.openings, ...(workspace.partyOpenings ?? [])],
    neighbourRooms: neighbourRoomsFor(workspace, typology),
    commonAreaPositionIds: commonAreaPositions(workspace),
    ownPositionIds: [positionOfTypology(typology.unitTypeId)],
    elements: estrutura.elements,
    elementSegments: estrutura.segments,
    // O buraco na laje sai do piso e do teto do ambiente dele (RN-271).
    shafts: typology.shafts ?? [],
    // A estaca é peça própria, e só a posicionada conta (Fase 27, RN-360, RN-364): ela entra na
    // conta pelos tipos da obra e pelas marcas da tipologia, e nunca pelo ambiente ou pela parede.
    pieces: desenhadas.pieces ?? [],
    pileTypes: workspace.pileTypes ?? [],
    pieceMarks: typology.pieceMarks ?? [],
    // O bloco e a viga baldrame são peças da mesma família (Fase 27, etapa 2): contam à parte, e o
    // baldrame desconta os blocos desta tipologia (RN-360, RN-368).
    pileCaps: desenhadas.pileCaps ?? [],
    groundBeams: desenhadas.groundBeams ?? [],
    // O radier e a laje também são peças (Fase 27, etapa 3): a laje perde os shafts das
    // tipologias do mesmo desenho (RN-370), e onde há laje a viga conta só abaixo dela (RN-371).
    slabs: desenhadas.slabs ?? [],
    ...(temLaje ? { slabOpenings: buracosDoAndar(workspace, typology) } : {}),
    ...(sobAsVigas === undefined ? {} : { slabThicknessOverBeams: sobAsVigas }),
    // O muro e o pilar de contenção (Fase 27, etapa 4): peças próprias, que se descontam entre si
    // dentro desta tipologia (RN-372) e que nenhuma medida de parede ou de pilar soma (RN-360).
    retainingWalls: desenhadas.retainingWalls ?? [],
    retainingColumns: desenhadas.retainingColumns ?? [],
    // A escada (etapa 5): peça própria, que nenhuma medida de ambiente soma — nem o ambiente
    // ESCADA, que mede o acabamento (RN-360, F17).
    stairs: desenhadas.stairs ?? [],
  });
  if (desenhadas === typology) return geometria;
  // A importada sai da conta dela, e continua descontando a vizinha (RN-368, RN-372).
  return {
    ...geometria,
    groundBeam: groundBeamTotals(desenhadas.groundBeams ?? [], typology.pileCaps ?? []),
    retainingWall: retainingWallTotals(
      desenhadas.retainingWalls ?? [],
      typology.retainingColumns ?? [],
    ),
    retainingColumn: retainingColumnTotals(
      desenhadas.retainingColumns ?? [],
      typology.retainingWalls ?? [],
    ),
  };
}

/** Os totais do desenho de uma tipologia, uma vez — sem os pavimentos. */
export function totaisDaTipologia(
  workspace: WorkspaceDraft,
  typology: TypologyDraft,
): GeometryTotals {
  return summarizeGeometry(geometriaDaTipologia(workspace, typology));
}

/**
 * A participação de cada bitola nos serviços, nesta tipologia — o que `TakeoffQuantities.gaugeShares`
 * espera (Fase 27, RN-378).
 *
 * A do serviço comum é a taxa da disciplina; a do serviço que mede a **estaca** é o aço dos tipos
 * das estacas posicionadas desta tipologia (RN-367) — por isso sai daqui, onde se sabe quais
 * estacas são dela, e não do catálogo sozinho.
 *
 * Na fundação com taxa por peça (Fase 41, etapa 2 — RN-546), a do bloco, do baldrame e do radier
 * é a taxa equivalente das peças desta tipologia (`fundacao`, de `fundacaoDaTipologia`).
 */
export function participacoesDaTipologia(
  catalog: Pick<ServiceCatalog, 'disciplines'>,
  totals: Pick<GeometryTotals, 'pile'>,
  fundacao?: FoundationFactors,
): Readonly<Record<string, Readonly<Partial<Record<SteelGauge, number>>>>> {
  return catalogGaugeShares(catalog, totals.pile, fundacao);
}

/**
 * A quantidade de cada serviço do catálogo nesta tipologia, pela chave do serviço.
 *
 * O serviço leva a taxa de aço da disciplina dele (Fase 27, ADR-176): é o que a medida `ACO`
 * multiplica pelo volume da peça. Sem taxa, o aço não mede — e o item de aço não calcula.
 *
 * A quantidade do serviço nas peças da planilha por peça soma-se ao que o desenho mede das
 * outras (Fase 28, RN-384, RN-388) — e perde para o número que a tipologia digitou
 * (`comAsPecas`). `totals` já vem sem as peças importadas (`geometriaDaTipologia`).
 *
 * Com taxa de aço por peça na fundação (Fase 41, etapa 2 — RN-545; ADR-187), o serviço de aço do
 * bloco, do baldrame e do radier leva a taxa equivalente das peças (`foundationSteelRates`): o
 * volume total × a soma dela é Σ volume × taxa, peça a peça. Sem nenhuma taxa de peça nem de
 * tipo, é a da disciplina, e o número é o de antes.
 */
export function quantidadesDosServicos(
  catalog: ServiceCatalog,
  totals: GeometryTotals,
  typology: TypologyDraft,
  fundacao?: FoundationFactors,
): ReadonlyMap<string, ServicoComPecas> {
  const dasPecas = pecasDosServicos(typology.pieceQuantities);
  return new Map(
    catalog.disciplines.flatMap((disciplina) =>
      disciplina.services.map((service): [string, ServicoComPecas] => {
        const decisao = typology.serviceQuantities?.[service.key];
        return [
          service.key,
          comAsPecas(
            resolveServiceQuantity({
              service: withSteelRate(
                service,
                foundationSteelRates(service, fundacao, taxaDoServico(disciplina, service)) ??
                  taxaDoServico(disciplina, service),
              ),
              totals,
              ...(decisao === undefined ? {} : { decision: decisao }),
            }),
            dasPecas[service.key],
          ),
        ];
      }),
    ),
  );
}

/**
 * O medido de cada serviço — o que `TakeoffQuantities.measured` espera.
 *
 * Só o serviço que tem número entra: o pendente e o digitado ainda em branco ficam de fora,
 * e o índice deles não calcula nada.
 *
 * Com peça importada, é a base do índice, e não o medido inteiro (Fase 28, RN-384): o item
 * da peça já vem pronto, e o calculado só mede as outras (`baseDoIndice`).
 */
export function medidosDaTipologia(
  catalog: ServiceCatalog,
  totals: GeometryTotals,
  typology: TypologyDraft,
  /** A fundação com taxa por peça (Fase 41, RN-545), de `fundacaoDaTipologia`. */
  fundacao?: FoundationFactors,
): Readonly<Record<string, number>> {
  const medidos: Record<string, number> = {};
  for (const [chave, resolvido] of quantidadesDosServicos(catalog, totals, typology, fundacao)) {
    const base = baseDoIndice(resolvido);
    if (base !== undefined) medidos[chave] = base;
  }
  return medidos;
}
